import type { Skill } from '../content/types.ts';
import type { CrawlerType } from '../domain/character.ts';

/**
 * Whether a Skill may be rolled at all, and if not, why.
 *
 * NOTE: this no longer looks at the Skill's Common Stat. Which Stat applies is
 * the player's choice (RULE DCC-CHK-006), so the Stat is supplied separately at
 * roll time rather than being read off the Skill.
 *
 * RULE DCC-CHK-005 - Passive Skills do not require and cannot make a Skill
 * Check. Source: Rules for Survival p.12.
 * RULE DCC-CHK-004 - A Spell Skill cannot be attempted without Ranks (unless
 * it is a scroll). Source: Rules for Survival p.12.
 * RULE DCC-CHK-003 - Attack and Utility Skills may be attempted untrained, with
 * Disadvantage and no Ranks. Source: Rules for Survival p.12.
 */
export type Rollability =
  | { rollable: true; untrained: boolean }
  | { rollable: false; reason: string };

export function canRollSkill(skill: Skill, rank: number): Rollability {
  // RULE DCC-SKILL-002 - the TRAIT, not the kind. Heal is a Spell that makes no
  // Check ("Interrupt, Passive", C&C p.38), so asking `kind` here would have
  // made it rollable the moment it became `kind: 'spell'`.
  if (skill.traits.includes('passive')) {
    return {
      rollable: false,
      reason: 'Passive Skills do not make Skill Checks (Rules for Survival p.12).',
    };
  }
  if (skill.kind === 'spell' && rank < 1) {
    return {
      rollable: false,
      reason:
        'A Spell Skill cannot be attempted without Ranks unless it is a scroll (Rules for Survival p.12).',
    };
  }
  return { rollable: true, untrained: rank < 1 };
}

/** Animal-only Skills are not legal for human crawlers. Source: Crawlers & Customization p.10. */
export function isSkillLegalFor(skill: Skill, crawlerType: CrawlerType): boolean {
  if (skill.restriction === 'animal-only') return crawlerType === 'animal';
  return true;
}
